import React, { useEffect, useState } from 'react';
import {
  AddUniversal,
  MoreHorizontalUniversal,
  TrashCanOutlined,
  WarningAltOutlined,
} from '@duxton-web/icons';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Flex,
  Form,
  Popover,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { useQueries } from '@tanstack/react-query';
import styled from 'styled-components';

import Service from '../../../lib/App';
import { Constraint, ConstraintType } from '../../../lib/PlannerDTOs';
import { ExperimentAssignmentStrategy, ExpFormField } from '../helper/consts';
import FullDivider from './FullDivider';

import './ExperimentConstraint.css';

import ConstraintEditor, {
  getProperlyInstrumentedFacetList,
  useVariableStatsQuery,
} from '@/common/ConstraintsEditor/Constraint';
import { ConstraintOperator } from '@/common/ConstraintsEditor/Constraint/facetMap';
import { isProduction } from '@/common/environment';
import { detectValueItemType } from '@/common/valueItemType';
import { FACETS } from '@/constants/facets';

const { Text } = Typography;

const UNSELECTABLE_CONSTRAINT_FACETS: string[] = ['ts', 'city', 'ctry'];

const getAllowableFacets = (
  assignedStrategy: string,
  variableFacets: string[],
) => {
  const facets = Service.cache.getAllFacets();
  return facets
    .filter(
      (facet) =>
        facet.strategies.length === 0 ||
        facet.strategies.includes(assignedStrategy) ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE_V2 ||
        assignedStrategy === ExperimentAssignmentStrategy.CUSTOM_TIME_SLICE ||
        assignedStrategy === ExperimentAssignmentStrategy.TIME_SLICE_SHUFFLE,
    )
    .filter((facet) => variableFacets.includes(facet.name))
    .filter((facet) => !UNSELECTABLE_CONSTRAINT_FACETS.includes(facet.name))
    .map((facet) => {
      return { value: facet.name, label: facet.title };
    });
};

interface ExperimentConstraintProps {
  isViewMode?: boolean;
}

const ExperimentConstraint = ({
  isViewMode = false,
}: ExperimentConstraintProps) => {
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string>(null);
  const [isConstraintError, setIsConstraintError] = useState<number>(null);
  const [variablesFacets, setVariableFacets] = useState<string[]>([]);
  const [facetCheck, setFacetCheck] = useState({
    shouldDisplay: false,
    skipFacetCheck: false,
  });

  const form = Form.useFormInstance();
  form.setFieldValue('facetCheck', facetCheck);
  const currentStrategyName = Form.useWatch(ExpFormField.STRATEGY.NAME, {
    form,
    preserve: true,
  });

  const currentConstraints = Form.useWatch(ExpFormField.CONSTRAINTS, {
    form,
    preserve: true,
  });

  useEffect(() => {
    // clear constraints state whenever form value constraints is reset
    if (currentConstraints?.length === 0) {
      setConstraints([]);
    }
  }, [currentConstraints]);

  useEffect(() => {
    const constraints = form.getFieldValue(ExpFormField.CONSTRAINTS);
    if (!constraints?.length) return;

    const transformedConstraints = constraints.map((constraint) => {
      if (
        !constraint.valueItemType &&
        (constraint.type == ConstraintType.Standard || !constraint.type)
      ) {
        return {
          ...constraint,
          valueItemType: detectValueItemType(constraint),
        };
      }
      return constraint;
    });
    form.setFieldValue(ExpFormField.CONSTRAINTS, transformedConstraints);
    setConstraints(transformedConstraints);
  }, []);

  const variables =
    Form.useWatch(['variables'], {
      form,
      preserve: true,
    }) || {};
  const variableStats = useVariableStatsQuery(Object.keys(variables));
  const properlyInstrumentedFacetList = getProperlyInstrumentedFacetList(
    variableStats,
  ) as string[];

  const q = variables
    ? Object.keys(variables).map((variable) => {
        return {
          queryKey: ['variableFacets', variable],
          queryFn: () => Service.planner.fetchVariable(variable),
        };
      })
    : [];

  const queries = useQueries({ queries: q });

  useEffect(() => {
    const allConcurrentRequestsCompleted = queries.every(
      (query) => query.isSuccess,
    );
    if (
      queries.length > 0 &&
      allConcurrentRequestsCompleted &&
      variablesFacets.length === 0
    ) {
      const set = new Set();
      queries.forEach((query) => {
        query?.data?.facets?.forEach((facet) => {
          set.add(facet);
        });
      });
      setVariableFacets(Array.from(set) as string[]);
    }
  }, [queries, variablesFacets]);

  const handleAddConstraint = (whichType: ConstraintType) => {
    let defaultConstraint: Constraint = {
      target: '',
      op: '',
      type: ConstraintType.Standard,
      value: '',
    };

    switch (whichType) {
      case ConstraintType.CustomString:
        defaultConstraint = Object.assign(defaultConstraint, {
          type: ConstraintType.CustomString,
        });
        break;
      case ConstraintType.CustomInt:
        defaultConstraint = Object.assign(defaultConstraint, {
          type: ConstraintType.CustomInt,
        });
        break;
      default:
        break;
    }

    form.setFieldValue(ExpFormField.CONSTRAINTS, [
      ...constraints,
      defaultConstraint,
    ]);

    setIsConstraintError(null);
    setConstraints([...constraints, defaultConstraint]);
  };

  const shouldDisplayFacetBanner = (constraints: Constraint[]) => {
    let shouldDisplay = false;

    if (!isProduction) return shouldDisplay;

    for (let constraint of constraints) {
      if (
        !properlyInstrumentedFacetList.includes(constraint.target) &&
        constraint.target !== FACETS.Timestamp
      ) {
        shouldDisplay = true;
      }
    }
    return shouldDisplay;
  };

  const handleRemoveConstraint = (index: number) => {
    const copyConstraints = [...constraints];
    const filteredConstraints = [
      ...copyConstraints.slice(0, index),
      ...copyConstraints.slice(index + 1),
    ];
    const shouldDisplay = shouldDisplayFacetBanner(filteredConstraints);

    setFacetCheck({
      skipFacetCheck: shouldDisplay ? facetCheck.skipFacetCheck : false,
      shouldDisplay,
    });
    setIsConstraintError(null);
    setConstraints(filteredConstraints);
    form.setFieldValue(ExpFormField.CONSTRAINTS, filteredConstraints);
  };

  const handleUpdateConstraint = (index: number, constraint: Constraint) => {
    const copyConstraints = [...constraints];
    copyConstraints[index] = Object.assign({}, constraint);
    const shouldDisplay = shouldDisplayFacetBanner(copyConstraints);

    setConstraints([...copyConstraints]);
    setFacetCheck({
      ...facetCheck,
      shouldDisplay,
    });
    form.setFieldValue(ExpFormField.CONSTRAINTS, [...copyConstraints]);
  };

  // Temp fix to only show some operators to users
  const showConstraint = (constraint: Constraint) => {
    switch (constraint.op) {
      case ConstraintOperator.SLOT.value:
        return false;
      default:
        return true;
    }
  };

  const displayableConstraints = constraints?.filter((constraint) => {
    return (
      showConstraint(constraint) && constraint.type !== ConstraintType.Targeting
    );
  });

  return (
    <>
      {isViewMode && !displayableConstraints?.length ? null : (
        <FullDivider
          orientation="left"
          orientationMargin={0}
          style={{ marginTop: 8, marginBottom: 8 }}
        >
          <Tag color="#3D3D3D" style={{ borderRadius: 8 }}>
            Constraints
          </Tag>
        </FullDivider>
      )}

      {displayableConstraints?.map((constraint: Constraint, index: number) => (
        <Flex vertical gap={8} key={`${index}-${constraint.target}`}>
          <ConstraintRow>
            {isViewMode && showConstraint(displayableConstraints[index]) && (
              <>
                <ConstraintTag
                  className={
                    index === isConstraintError ? 'antd-error-state' : ''
                  }
                >
                  AND
                </ConstraintTag>
                <ConstraintEditor
                  index={index}
                  isCustomConstraint={
                    constraint.type !== ConstraintType.Standard
                  }
                  allowedFacets={getAllowableFacets(
                    currentStrategyName,
                    variablesFacets,
                  )}
                  hasError={index === isConstraintError}
                  constraint={displayableConstraints[index]}
                  onChange={handleUpdateConstraint}
                  disabled={isViewMode}
                  showFacetStatus
                />
              </>
            )}
            {!isViewMode && (
              <>
                <ConstraintTag
                  className={
                    index === isConstraintError ? 'antd-error-state' : ''
                  }
                >
                  AND
                </ConstraintTag>
                <ConstraintEditor
                  index={index}
                  isCustomConstraint={
                    constraint.type !== ConstraintType.Standard
                  }
                  allowedFacets={getAllowableFacets(
                    currentStrategyName,
                    variablesFacets,
                  )}
                  hasError={index === isConstraintError}
                  constraint={displayableConstraints[index]}
                  onChange={handleUpdateConstraint}
                  disabled={isViewMode}
                  showFacetStatus
                />
                <ConfigProvider
                  theme={{
                    token: {
                      fontWeightStrong: 400,
                    },
                    components: {
                      Button: {
                        onlyIconSize: 14,
                        defaultBorderColor: '#bfbfbf',
                      },
                    },
                  }}
                >
                  <Button
                    shape="default"
                    className={
                      index === isConstraintError ? 'antd-error-state-btn' : ''
                    }
                    icon={<TrashCanOutlined />}
                    onClick={() => handleRemoveConstraint(index)}
                    onMouseOver={() => {
                      setIsConstraintError(index);
                    }}
                    onMouseOut={() => {
                      setIsConstraintError(null);
                    }}
                  />
                </ConfigProvider>
              </>
            )}
          </ConstraintRow>
        </Flex>
      ))}
      {error && <Text type="danger">{error}</Text>}

      {!isViewMode && (
        <ConstraintRow>
          <ConstraintTag>AND</ConstraintTag>
          <ConfigProvider
            theme={{
              token: {
                fontWeightStrong: 400,
              },
              components: {
                Button: {
                  contentFontSize: 14,
                  defaultBorderColor: '#bfbfbf',
                },
              },
            }}
          >
            <Tooltip
              title={
                !currentStrategyName &&
                'Please select an assignment strategy first!'
              }
              placement="bottom"
            >
              <Button
                shape="default"
                disabled={!currentStrategyName}
                icon={<AddUniversal />}
                onClick={() => handleAddConstraint(null)}
              >
                Add Constraint
              </Button>
            </Tooltip>

            <Popover
              content={
                <Flex vertical gap={8}>
                  <Button
                    shape="default"
                    ghost
                    icon={<AddUniversal />}
                    onClick={() =>
                      handleAddConstraint(ConstraintType.CustomString)
                    }
                  >
                    Custom Constraint (String)
                  </Button>

                  <Button
                    ghost
                    shape="default"
                    icon={<AddUniversal />}
                    onClick={() =>
                      handleAddConstraint(ConstraintType.CustomInt)
                    }
                  >
                    Custom Constraint (Integer)
                  </Button>
                </Flex>
              }
              trigger="click"
            >
              <Button
                shape="default"
                disabled={!currentStrategyName}
                icon={<MoreHorizontalUniversal />}
              />
            </Popover>
          </ConfigProvider>
        </ConstraintRow>
      )}

      {!isViewMode && facetCheck.shouldDisplay && (
        <>
          <FacetCheckBanner>
            <Text strong>
              Some constraint facets may not be implemented in code
            </Text>
            <br />
            <Text style={{ color: '#707070', fontWeight: 400 }}>
              There is no data available for the facets of the constraints
              marked in (<WarningAltOutlined style={{ color: '#faad14' }} />
              ). If they are not implemented in code, these facets will have no
              effect.
            </Text>
            <SkipFacetCheck>
              <Form.Item
                name="facetCheck"
                style={{ marginBottom: 0 }}
                rules={[
                  {
                    required: true,
                  },
                  () => ({
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    validator(_, __) {
                      const { shouldDisplay, skipFacetCheck } = facetCheck;
                      if (shouldDisplay && !skipFacetCheck) {
                        return Promise.reject(
                          new Error('Please select the checkbox'),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Checkbox
                  onChange={(e) => {
                    setFacetCheck({
                      ...facetCheck,
                      skipFacetCheck: e.target.checked,
                    });
                  }}
                >
                  <Text strong>Skip Facet Check</Text>
                </Checkbox>
              </Form.Item>
            </SkipFacetCheck>
          </FacetCheckBanner>
        </>
      )}
    </>
  );
};

const ConstraintRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 0;
  align-items: stretch;
`;

const ConstraintTag = styled(Tag)`
  border-radius: 8px;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0px;
`;

const FacetCheckBanner = styled.div`
  min-width: 100%;
  min-height: 74px;
  background: #fff9c0;
  border: 1px solid#FFD933;
  border-radius: 12px;
  padding: 14px 16px;
  margin-top: 8px;
`;

const SkipFacetCheck = styled.div`
  min-width: 100%;
  margin-top: 4px;
`;

export default ExperimentConstraint;
